import { useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import {
  OutsourceInvoiceWithProject,
  loaders,
  invoiceControlConfigByCode,
  outsourceInvoiceConfigByCode,
  outsourceInvoiceStatusConfig,
} from 'entities';

import { Skeleton, Table, IconButton, Button, Card } from 'components/Blocks';
import { CartArrowLeftIcon, ExternalLinkIcon, ReceiptLongIcon } from 'components/Icons';
import { EntityStatusTag } from 'components/Entities';

import { OutsourceInvoiceModal } from '../components';

import { getTimestampDisplay } from 'utils/date';
import { truncateText } from 'utils/text';

import { TabProps } from './types';

export function ActiveOutsourceInvoices(_props: TabProps): JSX.Element {
  const { t } = useLang();

  const [invoice, setInvoice] = useState<string | null>(null);

  const { data, errorOrLoading, overwrite } = loaders.invoice.activeOutsourceInvoices.use({
    defaultValue: [],
  });

  const onUpdate = useCallback(
    (newData: OutsourceInvoiceWithProject) => {
      const shouldRemove = [
        outsourceInvoiceStatusConfig.canceled.code,
        outsourceInvoiceStatusConfig.paid.code,
      ].some((status) => status === newData.status);

      overwrite((old) =>
        shouldRemove
          ? old.filter(({ id }) => id !== newData.id)
          : old.map((existing) => (existing.id === newData.id ? newData : existing)),
      );
    },
    [overwrite],
  );

  if (errorOrLoading) return <Skeleton quantity={7} height={32} gap="2" />;

  return (
    <>
      <OutsourceInvoiceModal invoice={invoice} close={() => setInvoice(null)} onUpdate={onUpdate} />

      <Card>
        <Table
          items={data}
          uniqueKey="id"
          spacing="medium"
          columns={[
            {
              className: 'w-10',
              render: () => <CartArrowLeftIcon color="primary" />,
            },
            {
              label: t('service') as string,
              property: 'serviceName',
              filter: true,
              sort: true,
            },
            {
              label: t('provider') as string,
              property: 'providerName',
              filter: true,
              sort: true,
            },
            {
              label: t('created-at') as string,
              property: 'createdAt',
              render: ({ createdAt }) => getTimestampDisplay(createdAt),
            },

            {
              label: t('status') as string,
              filter: true,
              filterSearch: false,
              getTextValue: ({ status }) => outsourceInvoiceConfigByCode[status].getLabel(t),

              render: ({ status }) => <EntityStatusTag target="outsourceInvoice" status={status} />,
            },
            {
              label: t('project') as string,
              search: true,
              getTextValue: ({ project }) => project.name,
              render: ({ project }) => (
                <Button
                  linkTarget={project.id}
                  tooltipContent={project.name}
                  tooltipDisabled={project.name.length < 35}
                  to="projectProfile"
                  params={{ projectId: project.id }}
                  variant="link"
                  color="gray"
                >
                  {truncateText(project.name, 35)}

                  <ExternalLinkIcon size={14} />
                </Button>
              ),
            },

            {
              label: t('invoice-status') as string,
              filter: true,
              filterSearch: false,
              getTextValue: ({ project }) =>
                invoiceControlConfigByCode[project.invoice.status].getLabel(t),

              render: ({ project }) => (
                <EntityStatusTag target="invoiceControl" status={project.invoice.status} />
              ),
            },
            {
              render: ({ id }) => (
                <IconButton tooltipContent={t('open')} onClick={() => setInvoice(id)}>
                  <ReceiptLongIcon />
                </IconButton>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
